import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableTagSelect from '@/components/common/SearchableTagSelect';
import { Zap, Save, CheckSquare } from 'lucide-react';
import RockTaskCreator from './RockTaskCreator';

const BALLOON_APPROACHES = [
  { value: 'reassign', label: 'Reassign / Redistribute' },
  { value: 'process_adjustment', label: 'Process Adjustment' },
  { value: 'policy_tweak', label: 'Policy Tweak' },
  { value: 'communication_fix', label: 'Communication Fix' },
  { value: 'deprioritize_monitor', label: 'Deprioritize and Monitor' }
];

const TRACK_STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

export default function BalloonResolutionPanel({ issue, officers, onComplete }) {
  const queryClient = useQueryClient();
  const [showTaskCreator, setShowTaskCreator] = useState(false);

  const [formData, setFormData] = useState({
    decision: issue?.decision || '',
    rock_track3_owner: issue?.rock_track3_owner || '',
    rock_track3_status: issue?.rock_track3_status || 'not_started',
    rock_track3_notes: issue?.rock_track3_notes || ''
  });

  const saveMutation = useMutation({
    mutationFn: (data) => base44.entities.Issue.update(issue.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      onComplete?.();
    }
  });

  const officerOptions = (officers || []).map((o) => ({
    value: o.user_email,
    label: `${o.callsign} (${o.rank_title})`
  }));

  const handleSave = () => {
    saveMutation.mutate(formData);
  };

  const borderColor = 'border-amber-500/30';
  const textColor = 'text-amber-400';

  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="text-sky-600 text-lg font-bold">🎈 Balloon Operational Response</h3>
        <p className="text-xs text-slate-500 mt-1">What can we do right now, with what we have, to mitigate the impact?</p>
      </div>

      <div className="bg-zinc-100 p-4 rounded-lg border border-amber-500/30 space-y-3">
        <div className="flex items-center gap-2">
          <Zap className={`w-5 h-5 ${textColor}`} />
          <h4 className={`text-sm font-bold ${textColor}`}>Operational Response</h4>
        </div>

        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium">Approach</Label>
          <Select value={formData.decision} onValueChange={(v) => setFormData((p) => ({ ...p, decision: v }))}>
            <SelectTrigger className="bg-slate-950 border-amber-500/20 h-8 text-sm text-slate-50"><SelectValue placeholder="Select approach..." /></SelectTrigger>
            <SelectContent>
              {BALLOON_APPROACHES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium">Track Owner</Label>
          <SearchableTagSelect
            options={officerOptions}
            value={formData.rock_track3_owner}
            onValueChange={(v) => setFormData((p) => ({ ...p, rock_track3_owner: v }))}
            placeholder="Assign owner..."
            emptyMessage="No officers."
          />
        </div>
        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium">Status</Label>
          <Select value={formData.rock_track3_status} onValueChange={(v) => setFormData((p) => ({ ...p, rock_track3_status: v }))}>
            <SelectTrigger className="bg-slate-950 border-amber-500/20 h-8 text-sm text-slate-50"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRACK_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-amber-500 text-xs font-medium">Notes</Label>
          <Textarea data-testid="issues-balloonresolutionpanel-textarea"
            value={formData.rock_track3_notes}
            onChange={(e) => setFormData((p) => ({ ...p, rock_track3_notes: e.target.value }))}
            className="bg-slate-950 border-amber-500/20 text-slate-200 text-sm"
            rows={2}
            placeholder="Operational response notes..."
          />
        </div>
        <Button data-testid="issues-create-task-btn" size="sm" variant="outline" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10" onClick={() => setShowTaskCreator(true)}>
          <CheckSquare className="w-3.5 h-3.5 mr-1" /> Create Task
        </Button>
      </div>

      <Button onClick={handleSave} disabled={saveMutation.isPending} className="w-full bg-sky-600 hover:bg-sky-700 text-white">
        <Save className="w-4 h-4 mr-2" />
        {saveMutation.isPending ? 'Saving...' : 'Save Track'}
      </Button>

      <RockTaskCreator
        open={showTaskCreator}
        onClose={() => setShowTaskCreator(false)}
        issueId={issue?.id}
        officers={officers}
      />
    </div>
  );
}